import { observer } from "mobx-react-lite"
import { useEffect } from "react"

import type { ILock } from "@/domains/lock"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MAX_NFTS_PER_WALLET } from "@/features/lock/src/constants"

import { CastYourVoteCardView } from "./cast-your-vote-card-view"
import { ManagedSelectionDrawerObserver } from "./managed-selection-drawer-observer"

/**
 * Observer component that connects to MobX stores.
 * All state and computation is handled by VotePageStore.
 * This component simply observes store properties and passes them to the pure View component.
 */
export const CastYourVoteCard = observer(() => {
  const scaleApp = useScaleApplication()

  if (!scaleApp) {
    return null
  }

  const votePageStore = scaleApp.Pages.Vote.Store
  const selectedPowerStore = votePageStore.selectedPowerStore

  if (!selectedPowerStore) {
    return null
  }

  // Observe SelectedPowerStore properties directly - breaks circular dependency
  const selectedLocks = selectedPowerStore.selectedLocksAsDTOs
  const totalSelectedPower = selectedPowerStore.selectedVotePower
  const isDrawerOpen = votePageStore.isCastYourVoteDrawerOpen
  const selectedLockIds = selectedPowerStore.selectedNftIds
  const activeLocks = selectedPowerStore.activeLocks
  const totalActiveLocks = activeLocks.length

  // Ensure vote data is loaded when drawer opens
  useEffect(() => {
    if (isDrawerOpen) {
      // Load vote data for all locks to ensure it's available
      // Vote data is automatically loaded when locks are added, but this ensures
      // all existing locks have their vote data loaded when the drawer opens
      const lockOrchestrator = scaleApp.Lock.Orchestrator
      if (lockOrchestrator) {
        void lockOrchestrator.loadLastEpochVotesForAllLocks()
      }
    }
  }, [isDrawerOpen, scaleApp])

  return (
    <>
      <CastYourVoteCardView
        totalSelectedPower={totalSelectedPower}
        selectedLocks={selectedLocks}
        totalActiveLocks={totalActiveLocks}
        onManageSelection={() => votePageStore.openCastYourVoteDrawer()}
        onRemoveLock={(lockId) => selectedPowerStore.toggleNftSelection(lockId)}
      />

      {isDrawerOpen && (
        <ManagedSelectionDrawerObserver
          activeLocks={selectedPowerStore.activeLocksAsDTOs}
          inactiveLocks={selectedPowerStore.inactiveLocksAsDTOs}
          selectedLockIds={selectedLockIds}
          onToggleLock={(lockId) => selectedPowerStore.toggleNftSelection(lockId)}
          onSelectAll={() => {
            // Select all available (active) locks, up to MAX_NFTS_PER_WALLET
            const activeLockIds = selectedPowerStore.activeLocksAsDTOs
              .map((lock) => lock.id)
              .slice(0, MAX_NFTS_PER_WALLET)
            selectedPowerStore.setSelectedNfts(activeLockIds)
          }}
          onDeselectAll={() => {
            // Deselect all currently selected locks
            selectedPowerStore.setSelectedNfts([])
          }}
          onClose={() => votePageStore.closeCastYourVoteDrawer()}
        />
      )}
    </>
  )
})
